import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Check, Footprints } from "lucide-react";
import { API } from "../data/constants";
import { useCurrency } from "../contexts/CurrencyContext";

const BUSINESS_TYPES = [
  "Restaurant / Café",
  "Retail / Specialty Store",
  "Salon / Spa / Wellness",
  "Auto / Home Services",
  "Healthcare / Clinic",
  "Other"
];

const GOALS = [
  "Drive more in-store visits",
  "Acquire new local customers",
  "Increase repeat visits / loyalty",
  "Compete with nearby businesses",
  "Launch / Grand opening push"
];

const BENEFITS = [
  "Personalized SmartReach™ plan",
  "Hyperlocal targeting recommendations",
  "Estimated visit-lift potential",
  "No commitment, no obligation"
];

const INITIAL_FORM = {
  name: "",
  business_name: "",
  email: "",
  phone: "",
  website: "",
  business_type: "",
  locations: "",
  primary_goal: "",
  notes: ""
};

const inputBaseClass =
  "w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary focus:border-transparent outline-none transition";

// Reusable form field
const Field = ({ label, name, type = "text", value, onChange, required, placeholder, testId }) => (
  <div>
    <label className="block text-sm font-medium mb-1">
      {label}
      {required && " *"}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className={inputBaseClass}
      data-testid={testId}
    />
  </div>
);

// Reusable select
const SelectField = ({ label, name, value, onChange, options, placeholder, testId }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <select name={name} value={value} onChange={onChange} className={inputBaseClass} data-testid={testId}>
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </div>
);

// Left-side pitch column
const PitchColumn = ({ title, subtitle, isDark }) => (
  <div className="lg:col-span-2">
    <span
      className={`inline-flex items-center gap-2 font-semibold text-sm mb-4 px-4 py-2 rounded-full ${
        isDark ? "bg-white/10 text-orange-secondary" : "bg-orange-light text-orange-primary"
      }`}
    >
      <Footprints size={16} /> Free Custom Audit
    </span>
    <h2
      className={`font-display text-3xl md:text-4xl font-bold mb-4 leading-tight ${
        isDark ? "text-white" : "text-gray-dark"
      }`}
    >
      {title}
    </h2>
    <p className={`mb-6 text-lg ${isDark ? "text-gray-300" : "text-gray-medium"}`}>{subtitle}</p>
    <ul className="space-y-3">
      {BENEFITS.map((item) => (
        <li key={item} className="flex items-center gap-3" data-testid={`audit-benefit-${item.replace(/\s+/g, "-").toLowerCase()}`}>
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
              isDark ? "bg-orange-primary/20" : "bg-orange-light"
            }`}
          >
            <Check size={14} className="text-orange-primary" />
          </div>
          <span className={isDark ? "text-gray-200" : "text-gray-dark"}>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

// Status banner
const StatusBanner = ({ status }) =>
  status ? (
    <div
      className={`mt-4 p-4 rounded-lg ${
        status.type === "success"
          ? "bg-green-50 text-green-700 border border-green-200"
          : "bg-red-50 text-red-700 border border-red-200"
      }`}
      data-testid="audit-status-message"
    >
      {status.message}
    </div>
  ) : null;

/**
 * Foot-Traffic Audit Lead-Gen Form — embeddable on /smartreach and /foot-traffic-marketing.
 * Submits to /api/foot-traffic-audit which routes via Mailgun (US/UAE region-aware).
 */
export const FootTrafficAuditForm = ({
  title = "Request a Free Foot-Traffic Audit",
  subtitle = "Tell us about your business. We'll send back a custom plan to drive walk-ins within 24 hours.",
  variant = "light"
}) => {
  const { isUAE } = useCurrency();
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const res = await axios.post(`${API}/foot-traffic-audit`, {
        ...formData,
        region: isUAE ? "UAE" : "US"
      });
      setSubmitStatus({ type: "success", message: res.data.message });
      setFormData(INITIAL_FORM);
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message:
          error.response?.data?.detail ||
          "Something went wrong. Please try again or email us directly."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDark = variant === "dark";

  return (
    <section
      className={`section-padding ${isDark ? "bg-gradient-to-br from-gray-dark via-gray-900 to-gray-dark text-white" : "bg-white"}`}
      data-testid="foot-traffic-audit-section"
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          <PitchColumn title={title} subtitle={subtitle} isDark={isDark} />

          <div className="lg:col-span-3 bg-white rounded-2xl p-6 md:p-8 shadow-card text-gray-dark">
            <form onSubmit={handleSubmit} data-testid="foot-traffic-audit-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Your Name" name="name" value={formData.name} onChange={handleChange} required testId="audit-name-input" />
                <Field label="Business Name" name="business_name" value={formData.business_name} onChange={handleChange} required testId="audit-business-input" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Field label="Email" type="email" name="email" value={formData.email} onChange={handleChange} required testId="audit-email-input" />
                <Field label="Phone" type="tel" name="phone" value={formData.phone} onChange={handleChange} required testId="audit-phone-input" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Field label="Website" type="url" name="website" value={formData.website} onChange={handleChange} placeholder="https://" testId="audit-website-input" />
                <Field label="# of Locations" name="locations" value={formData.locations} onChange={handleChange} placeholder="e.g., 1, 3, 10+" testId="audit-locations-input" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <SelectField label="Business Type" name="business_type" value={formData.business_type} onChange={handleChange} options={BUSINESS_TYPES} placeholder="Select type" testId="audit-business-type-select" />
                <SelectField label="Primary Goal" name="primary_goal" value={formData.primary_goal} onChange={handleChange} options={GOALS} placeholder="Select goal" testId="audit-goal-select" />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium mb-1">Anything else we should know?</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Current marketing, biggest challenge, etc."
                  className={`${inputBaseClass} resize-none`}
                  data-testid="audit-notes-input"
                />
              </div>

              <StatusBanner status={submitStatus} />

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="audit-submit-btn"
              >
                {isSubmitting ? "Submitting..." : "Request My Free Audit"}{" "}
                {!isSubmitting && <ArrowRight size={18} className="inline ml-2" />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FootTrafficAuditForm;
